import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { TIMELINE_FILTERS, type LeadCardResponse, type TimelineFilter } from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { Button } from "../../design/components/Button";
import { Reason } from "../../design/components/Reason";
import { useToast } from "../../design/components/Toast";
import { TextField } from "../../design/components/TextField";
import { ToggleButton } from "../../design/components/ToggleButton";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { NOTE_TARGET_ID } from "../card/card-fields";
import { withoutControlCharacters } from "../lead-display";
import { timelineQuery, useAddLeadNote } from "../lead-queries";
import { NoteComposer } from "./NoteComposer";
import { TimelineItem } from "./TimelineItem";
import "../lead-list.css";
import "./timeline.css";

// La Timeline della scheda Lead (piano M2 Task 19, mock-up `scheda-lead.html` e `initLeadCard`): la colonna centrale,
// con i filtri, «Cerca nella Timeline», il riquadro «Nuova nota» e gli elementi dal più recente. Le pagine arrivano a
// cursore mentre si scorre (le precedenti restano), e una ricerca o un filtro nuovi tengono in vista gli elementi di
// prima finché arrivano quelli nuovi. La prima pagina si legge insieme alla scheda (`LeadCardPage`): la Timeline non la
// ferma mai. «Storico di Close» c'è soltanto nei Lead importati (proposta 16 dell'importazione da Close); «Email» (ingresso dei
// Lead, I5) mostra gli eventi delle email di GoHighLevel; «WhatsApp», «Task» e «Appuntamenti e opportunità» del mock-up non ci sono ancora. «Vedi nella
// Timeline» di una casella della sequenza (piano M5, Task 12, come `m-protocol-detail` del mock-up) torna a «Tutto» senza
// ricerca, legge le pagine più vecchie finché la telefonata non arriva e la porta in vista con il focus.

/** Quanto si aspetta dopo l'ultimo tasto prima di cercare, come nell'elenco dei Lead. */
const SEARCH_DELAY_MS = 200;
/** Il limite della ricerca (`timelineQuerySchema`). */
const SEARCH_MAX_LENGTH = 200;
/** Da quanto prima della fine si legge la pagina dopo, perché arrivi prima che serva. */
const LOAD_AHEAD = "400px 0px";

const FILTER_LABELS: Readonly<Record<TimelineFilter, MessageKey>> = {
  all: "timeline.filters.all",
  calls: "timeline.filters.calls",
  notes: "timeline.filters.notes",
  changes: "timeline.filters.changes",
  history: "timeline.filters.history",
  email: "timeline.filters.email",
};

type Props = {
  tab: TabContext;
  leadId: string;
  view: string | null;
  card: LeadCardResponse;
  timeZone: string;
  /**
   * L'elemento da portare in vista, con il suo `id` nella pagina (`timelineAnchorId`): la telefonata di «Vedi nella Timeline» o
   * l'elemento aperto da «Attività» (SEZ-8); ogni richiesta è un oggetto nuovo, anche per lo stesso elemento.
   */
  shownItem?: { anchor: string } | null;
  /** L'elemento è in vista, oppure non c'è in nessuna pagina. */
  onItemShown?: () => void;
};

/**
 * Chiama `onReach` quando l'elemento entra (o sta per entrare) nella finestra, finché `active`. Senza
 * IntersectionObserver resta il pulsante «Mostra gli elementi più vecchi».
 */
function useReachEnd(active: boolean, onReach: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!active || !element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onReach();
    }, { rootMargin: LOAD_AHEAD });
    observer.observe(element);
    return () => observer.disconnect();
  }, [active, onReach]);
  return ref;
}

export function Timeline({ tab, leadId, view, card, timeZone, shownItem = null, onItemShown }: Props) {
  const t = useT();
  const toast = useToast();
  const searchId = useId();
  const titleId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const imported = card.lead.createdBy.fromClose;
  const filters = TIMELINE_FILTERS.filter((filter) => filter !== "history" || imported);
  const [chosen, setChosen] = useState<TimelineFilter>("all");
  const filter = filters.includes(chosen) ? chosen : "all";

  // Il testo della ricerca: la Timeline si rilegge 200 ms dopo l'ultimo tasto.
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const next = withoutControlCharacters(text).trim();
    if (next === q) return;
    const timer = setTimeout(() => setQ(next), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text, q]);

  const timeline = useInfiniteQuery({ ...timelineQuery(tab, leadId, filter, q, view), placeholderData: keepPreviousData });
  const { mutateAsync: addNote } = useAddLeadNote(tab, leadId, view);
  const { fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isFetchNextPageError } = timeline;

  // Gli elementi di tutte le pagine lette, una volta sola anche se una nota nuova li avesse spostati di pagina.
  const items = useMemo(() => {
    const seen = new Set<string>();
    return (timeline.data?.pages ?? []).flatMap((page) =>
      page.items.filter((item) => {
        const key = `${item.kind}:${item.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }),
    );
  }, [timeline.data]);

  // Il tipo dei campi dell'azienda dal modello: una modifica di «Fiche media» si legge in euro.
  const fieldTypes = useMemo(() => new Map(card.model.sections.flatMap((section) => section.fields.map((field) => [field.ref, field.type] as const))), [card.model]);

  // La pagina dopo non parte mentre la Timeline si sta rileggendo (dopo una modifica, una nota, il ritorno sulla scheda):
  // `fetchNextPage` annullerebbe la rilettura e aggiungerebbe la pagina ai dati di prima, e una modifica appena salvata
  // non comparirebbe. Finita la rilettura, la fine osservata la fa partire da sé.
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetching) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetching]);
  const canLoadMore = hasNextPage && !isFetching && !isFetchNextPageError && !timeline.isPlaceholderData;
  const endRef = useReachEnd(canLoadMore, loadMore);

  async function saveNote(body: string) {
    await addNote({ body });
    toast.show(t("timeline.composer.saved"), "success");
  }

  const addMemo = useCallback((callId: string, body: string) => addNote({ body, callId }), [addNote]);

  // «Vedi nella Timeline» e i link di «Attività»: prima «Tutto» senza ricerca (una richiesta nuova, durante il disegno, come vuole
  // React per lo stato che dipende da una prop), poi l'elemento in vista appena c'è, leggendo le pagine più vecchie.
  const [askedItem, setAskedItem] = useState(shownItem);
  if (shownItem !== askedItem) {
    setAskedItem(shownItem);
    if (shownItem !== null) {
      setChosen("all");
      setText("");
      setQ("");
    }
  }
  const placeholder = timeline.isPlaceholderData;
  const loaded = timeline.data !== undefined;
  useEffect(() => {
    if (shownItem === null || filter !== "all" || q !== "" || placeholder || !loaded) return;
    const target = document.getElementById(shownItem.anchor);
    if (target) {
      if (typeof target.scrollIntoView === "function") target.scrollIntoView({ block: "center", behavior: "smooth" });
      target.focus({ preventScroll: true });
      onItemShown?.();
    } else if (hasNextPage && !isFetchNextPageError) {
      if (!isFetching) void fetchNextPage();
    } else if (!isFetching) {
      // Non c'è in nessuna pagina (o la pagina dopo non arriva, e lo dice la Timeline): niente da portare in vista.
      onItemShown?.();
    }
  }, [shownItem, filter, q, placeholder, loaded, items, hasNextPage, isFetching, isFetchNextPageError, fetchNextPage, onItemShown]);

  function resetSearch() {
    setText("");
    setQ("");
    searchRef.current?.focus();
  }

  const busy = timeline.isPlaceholderData || timeline.isPending;
  let content: ReactNode;
  if (timeline.isError && !timeline.data) {
    content = (
      <div className="g-timeline__problem">
        <Reason tone="warning" role="alert">{messageForError(t, timeline.error)}</Reason>
        <Button onClick={() => void timeline.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else if (!timeline.data) {
    content = (
      <p className="g-timeline__state" role="status">
        {t("timeline.loading")}
      </p>
    );
  } else if (items.length === 0 && !timeline.isPlaceholderData) {
    content = q ? (
      <div className="g-timeline__state">
        <p>{t("timeline.noResults", { q })}</p>
        <Button onClick={resetSearch}>{t("timeline.resetSearch")}</Button>
      </div>
    ) : (
      <p className="g-timeline__state">{filter === "all" ? t("timeline.empty") : t("timeline.emptyFilter")}</p>
    );
  } else {
    content = (
      <>
        <ul className="g-timeline__list" aria-labelledby={titleId} aria-busy={busy}>
          {items.map((item) => (
            <TimelineItem
              key={`${item.kind}:${item.id}`}
              item={item}
              tab={tab}
              timeZone={timeZone}
              fieldTypes={fieldTypes}
              canAddNotes={card.permissions.canAddNotes}
              onAddMemo={addMemo}
            />
          ))}
        </ul>
        {isFetchNextPageError ? (
          <div className="g-timeline__problem">
            <Reason tone="warning" role="alert">{t("timeline.moreFailed")}</Reason>
            <Button aria-disabled={isFetching ? "true" : undefined} onClick={loadMore}>
              {t("common.retry")}
            </Button>
          </div>
        ) : null}
        {hasNextPage && !isFetchNextPageError ? (
          <div ref={endRef} className="g-timeline__more">
            {/* Il pulsante resta anche mentre legge (il focus di chi l'ha premuto non si perde): spento a parole. */}
            <Button variant="ghost" aria-disabled={canLoadMore ? undefined : "true"} onClick={loadMore}>
              {isFetchingNextPage ? t("timeline.loadingMore") : t("timeline.more")}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <section className="g-lead-card__timeline g-timeline" aria-labelledby={titleId}>
      <div className="g-timeline__head">
        <h2 id={titleId} className="g-lead-card__area-title">
          {t("timeline.title")}
        </h2>
        <div className="g-timeline__filters" role="group" aria-label={t("timeline.filtersLabel")}>
          {filters.map((option) => (
            <ToggleButton key={option} pressed={option === filter} onClick={() => setChosen(option)}>
              {t(FILTER_LABELS[option])}
            </ToggleButton>
          ))}
        </div>
        <div className="g-lead-search g-timeline__search">
          <TextField
            ref={searchRef}
            id={searchId}
            label={t("timeline.searchLabel")}
            hideLabel
            icon="search"
            type="search"
            value={text}
            maxLength={SEARCH_MAX_LENGTH}
            autoComplete="off"
            placeholder={t("timeline.searchLabel")}
            onChange={(event) => setText(event.target.value)}
          />
        </div>
        {card.permissions.canAddNotes ? (
          <NoteComposer
            id={NOTE_TARGET_ID}
            texts={{
              label: "timeline.composer.label",
              placeholder: "timeline.composer.placeholder",
              hint: "timeline.composer.hint",
              submit: "timeline.composer.submit",
              saving: "timeline.composer.saving",
              required: "timeline.composer.required",
            }}
            onSave={saveNote}
          />
        ) : null}
      </div>
      {content}
    </section>
  );
}
